import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  DARK_THEME_CLASSNAME,
  LIGHT_THEME_CLASSNAME,
  SYSTEM_THEME_CLASSNAME,
  getStoredTheme,
  isDarkSystemTheme,
  setTheme,
} from '~/core/theming';
import ClientOnly from '~/core/ui/ClientOnly';
import Icon from '~/core/ui/Icon';
import IconButton from '~/core/ui/IconButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/core/ui/Select';

const DarkModeToggle = ({
  showMobileLabel = false,
  className = '',
  insideApp = false,
}) => {
  const defaultTheme = useMemo(getStoredTheme, []);
  const [currentTheme, setCurrentTheme] = useState<string>(defaultTheme);
  const [expanded, setExpanded] = useState(false);
  const isDarkTheme = currentTheme === DARK_THEME_CLASSNAME;
  const isSystemTheme = currentTheme === SYSTEM_THEME_CLASSNAME;

  const getIcon = useMemo(() => {
    const shouldUseSystemDarkTheme = isSystemTheme && isDarkSystemTheme();
    if (isDarkTheme || shouldUseSystemDarkTheme) {
      return (
        <Icon
          icon="line-md:sunny-filled-loop-to-moon-alt-filled-loop-transition"
          className={'h-5'}
          width="1.25rem"
          style={{ minWidth: '1.25rem', padding: 0 }}
        />
      );
    }
    return (
      <Icon
        icon="line-md:moon-filled-alt-to-sunny-filled-loop-transition"
        className={'h-5'}
        width="1.25rem"
        style={{ minWidth: '1.25rem', padding: 0 }}
      />
    );
  }, [isSystemTheme, isDarkTheme]);

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  const isInsideApp = insideApp;

  return (
    <div className={className}>
      {/* Mobile View */}
      <div className="sm:hidden px-[.6rem] py-[.25rem]">
        <div
          className="inner flex gap-2 items-center justify-between"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="flex items-center gap-[.6rem] text-sm">
            <ClientOnly fallback={<span className={'h-5 w-5'} />}>
              {getIcon}
            </ClientOnly>
            {showMobileLabel && <Trans i18nKey={'common:theme'} />}
          </span>
          {expanded ? (
            <ChevronUpIcon className="h-4" />
          ) : (
            <ChevronDownIcon className="h-4" />
          )}
        </div>
        {expanded && (
          <div
            className={
              'flex flex-col border border-gray-300 dark:border-dark-800 [&>button]:flex [&>button]:gap-3 [&>button]:items-center [&>button]:py-[.45rem] [&>button]:px-[.45rem] text-sm mx-0 my-[0.65rem] p-1 rounded-lg border-l-[#0003] border-solid bg-white dark:bg-dark-800 dark:border-dark-600/70 translate-y-[-1px] -translate-x-[.825rem] w-[109%]  shadow-md dark:shadow-primary/40 dark:shadow-2xl'
            }
          >
            <button
              className="hover:bg-gray-100 dark:hover:bg-dark-700"
              onClick={() => {
                setCurrentTheme(LIGHT_THEME_CLASSNAME);
                setExpanded(false);
              }}
            >
              <Icon
                icon="line-md:moon-filled-alt-to-sunny-filled-loop-transition"
                className={'h-5'}
                width="1.25rem"
                style={{ minWidth: '1.25rem', padding: 0 }}
              />
              <Trans i18nKey={'common:lightTheme'} />
            </button>
            <button
              className="hover:bg-gray-100 dark:hover:bg-dark-700"
              onClick={() => {
                setCurrentTheme(DARK_THEME_CLASSNAME);
                setExpanded(false);
              }}
            >
              <Icon
                icon="line-md:sunny-filled-loop-to-moon-alt-filled-loop-transition"
                className={'h-5'}
                width="1.25rem"
                style={{ minWidth: '1.25rem', padding: 0 }}
              />
              <Trans i18nKey={'common:darkTheme'} />
            </button>
            <button
              className="hover:bg-gray-100 dark:hover:bg-dark-700"
              onClick={() => {
                setCurrentTheme(SYSTEM_THEME_CLASSNAME);
                setExpanded(false);
              }}
            >
              <Icon
                icon="line-md:computer"
                className={'h-5'}
                width="1.25rem"
                style={{ minWidth: '1.25rem', padding: 0 }}
              />
              <Trans i18nKey={'common:systemTheme'} />
            </button>
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden sm:block">
        <Select value={currentTheme} onValueChange={setCurrentTheme}>
          <SelectTrigger asChild>
            <IconButton
              aria-label={'Toggle dark mode'}
              data-cy={'dark-mode-toggle'}
              className={
                'flex items-center !rounded-full border-transparent transition-shadow' +
                ' !bg-transparent hover:shadow-xl dark:border-transparent' +
                ' dark:shadow-primary-500/50'
              }
            >
              <span hidden>
                <SelectValue />
              </span>
              <ClientOnly fallback={<span className={'h-5 w-5'} />}>
                {getIcon}
              </ClientOnly>
            </IconButton>
          </SelectTrigger>
          <SelectContent position={'popper'} sideOffset={5}>
            <SelectItem
              data-cy={'light-theme-button'}
              value={LIGHT_THEME_CLASSNAME}
            >
              <span className={'flex items-center space-x-2.5'}>
                <Icon
                  icon="line-md:moon-filled-alt-to-sunny-filled-loop-transition"
                  className={'h-5'}
                  width="1.25rem"
                  style={{ minWidth: '1.25rem', padding: 0 }}
                />
                <span>
                  <Trans i18nKey={'common:lightTheme'} />
                </span>
              </span>
            </SelectItem>
            <SelectItem
              data-cy={'dark-theme-button'}
              value={DARK_THEME_CLASSNAME}
            >
              <span className={'flex items-center space-x-2.5'}>
                <Icon
                  icon="line-md:sunny-filled-loop-to-moon-alt-filled-loop-transition"
                  className={'h-5'}
                  width="1.25rem"
                  style={{ minWidth: '1.25rem', padding: 0 }}
                />
                <span>
                  <Trans i18nKey={'common:darkTheme'} />
                </span>
              </span>
            </SelectItem>
            <SelectItem
              data-cy={'system-theme-button'}
              value={SYSTEM_THEME_CLASSNAME}
            >
              <span className={'flex items-center space-x-2.5'}>
                <Icon
                  icon="line-md:computer"
                  className={'h-5'}
                  width="1.25rem"
                  style={{ minWidth: '1.25rem', padding: 0 }}
                />
                <span>
                  <Trans i18nKey={'common:systemTheme'} />
                </span>
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default DarkModeToggle;
